import { INotificationTemplateDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";

/**
 * Asks for the new template's name. It is the ONE place the name rule lives now — before
 * the refactor the landing page and the editor's rename carried two different regexes
 * (the editor's rejected `-` and `_`, so a name the landing page allowed could never be
 * re-saved). The looser of the two wins here so nothing that used to be creatable stops
 * being creatable, and the message finally matches the pattern.
 *
 * The sheet itself is `KuikaNameModal`; what stays here is what is actually email-specific,
 * the name rules.
 */

/** Start with a letter; letters, digits, spaces, `-` and `_` after that. Minimum two characters. */
export const EMAIL_TEMPLATE_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9-_ ]+$/;
export const EMAIL_TEMPLATE_NAME_MESSAGE: string =
  "Template name must start with a letter and can only contain letters, numbers, spaces, hyphens and underscores.";

/**
 * The column behind this name is `nvarchar(50)` in `NotificationTemplateTableSchema`, while the
 * platform's EF model declares it `nvarchar(max)` — whichever migrator touched a database last
 * decides, and every database checked so far is the narrow one. Without this rule a longer name
 * reaches the user as a raw EF "error occurred while saving the entity changes". Remove it once
 * the column is widened; it is a limit we report, not one we want.
 */
export const EMAIL_TEMPLATE_NAME_MAX_LENGTH: number = 50;
export const EMAIL_TEMPLATE_NAME_LENGTH_MESSAGE: string = `Template name can be at most ${EMAIL_TEMPLATE_NAME_MAX_LENGTH} characters.`;

/** The confirm says what it is doing in its own words: a template is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

export interface IEmailTemplateNameModalProps {
  visible: boolean;
  /** Heading copy, so "Blank Email" and "Use template" can each say what they are doing. */
  title: string;
  /** One line under the heading saying what happens next. */
  subtitle?: string;
  /** Glyph from `public/images/common/`, so the two entry points don't look alike. */
  iconName?: string;
  confirmLabel: string;
  /**
   * Where the create stands. The ✓ is held on the confirm before the new template's editor
   * replaces the page, and a failed create leaves the sheet open on "Try again".
   */
  status: KuikaButtonStatus;
  /** Every template in the project — the uniqueness check runs against all of them. */
  templates: INotificationTemplateDto[];
  onCancel: () => void;
  onSubmit: (name: string) => void;
}

export class EmailTemplateNameModal extends React.PureComponent<IEmailTemplateNameModalProps> {
  /**
   * Case-insensitive, and deliberately NOT locale-aware: `toLowerCase` is kept exactly as
   * the two pre-refactor validators had it, so which duplicates get rejected does not
   * shift for Turkish-named projects.
   */
  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const templates: INotificationTemplateDto[] = this.props.templates ?? [];
    if (templates.length === 0) return;
    if (templates.some((tmp: INotificationTemplateDto): boolean => tmp.name.toLowerCase() === value.toLowerCase())) {
      throw new Error("This Template Name is used before.");
    }
  };

  render() {
    return (
      <KuikaNameModal
        visible={this.props.visible}
        title={this.props.title}
        subtitle={this.props.subtitle}
        iconName={this.props.iconName ?? "mail"}
        confirmLabel={this.props.confirmLabel}
        status={this.props.status}
        statusLabels={CREATE_STATUS_LABELS}
        fieldName="templateName"
        placeholder="Enter template name"
        rules={[
          { required: true, message: "Please enter a name" },
          { pattern: EMAIL_TEMPLATE_NAME_PATTERN, message: EMAIL_TEMPLATE_NAME_MESSAGE },
          { max: EMAIL_TEMPLATE_NAME_MAX_LENGTH, message: EMAIL_TEMPLATE_NAME_LENGTH_MESSAGE },
          { validator: this.existingNameValidator }
        ]}
        inputTestId="emailBuilderNameInput"
        wrapTestId="emailNameModal"
        // Scope hook: `createButton` is not unique across the designer, and the confirm
        // button lives in the modal footer rather than inside the form.
        className="kuika-email-name-modal"
        onCancel={this.props.onCancel}
        onSubmit={this.props.onSubmit}
      />
    );
  }
}
